import { Alert, Button, Card, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { PushBroadcastListDataFragment, PushBroadcastStatus } from '../../../../../../__gqltypes__';
import { SolidBadge } from '../../../../../../utils/components/SolidBadge';
import {
  formatPushUtc,
  PUSH_BROADCAST_BASE_URL,
  PUSH_BROADCAST_FINISHED_STATUSES,
  PUSH_BROADCAST_STATUS_TONE,
  PushPerson,
  pushPersonLabel,
} from '../utils';

type Props = {
  broadcasts: ReadonlyArray<PushBroadcastListDataFragment>;
  people: ReadonlyMap<string, PushPerson>;
  onCreate: () => void;
  // A finished broadcast into a new draft: how a one-account test becomes the real send.
  onDuplicate: (id: string) => void;
  duplicatingId: string | null;
};

function recipientsCell(broadcast: PushBroadcastListDataFragment): JSX.Element | string {
  if (broadcast.recipientsCount != null) {
    return broadcast.recipientsCount.toLocaleString();
  }
  if (broadcast.audience) {
    return (
      <span className={broadcast.audience.isCurrent ? '' : 'text-muted'}>
        ≈ {broadcast.audience.count.toLocaleString()} counted
        {broadcast.audience.isCurrent ? '' : ' (out of date)'}
      </span>
    );
  }
  return <span className="text-muted">not counted</span>;
}

function startedCell(broadcast: PushBroadcastListDataFragment, people: ReadonlyMap<string, PushPerson>) {
  if (broadcast.status === PushBroadcastStatus.SCHEDULED) {
    return (
      <>
        <div>Scheduled for {formatPushUtc(broadcast.sendAt)}</div>
        <small className="text-muted">by {pushPersonLabel(broadcast.startedBy, people)}</small>
      </>
    );
  }
  if (!broadcast.startedAt) {
    return <span className="text-muted">—</span>;
  }
  return (
    <>
      <div>{formatPushUtc(broadcast.startedAt)}</div>
      <small className="text-muted">by {pushPersonLabel(broadcast.startedBy, people)}</small>
    </>
  );
}

/** Newest first. A scheduled broadcast whose time passed without starting is flagged here, not only on its page. */
export function PushBroadcastList({ broadcasts, people, onCreate, onDuplicate, duplicatingId }: Props): JSX.Element {
  const overdue = broadcasts.filter(({ isScheduleOverdue }) => isScheduleOverdue);
  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span className="font-weight-bold">Push notifications</span>
        <Button size="sm" onClick={onCreate}>
          New push notification
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        {overdue.length > 0 ? (
          <Alert variant="danger" className="m-2">
            {overdue.length} scheduled broadcast{overdue.length > 1 ? 's' : ''} never started at{' '}
            {overdue.length > 1 ? 'their' : 'its'} time (the scheduled task was lost, nothing was sent). Open{' '}
            {overdue.length > 1 ? 'them' : 'it'} and use &quot;Send now instead&quot;, or take{' '}
            {overdue.length > 1 ? 'them' : 'it'} back to draft.
          </Alert>
        ) : null}
        {broadcasts.length === 0 ? (
          <div className="text-muted p-3">No push notification yet.</div>
        ) : (
          <Table hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th className="text-right">Recipients</th>
                <th>Created</th>
                <th>Started (UTC)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((broadcast) => (
                <tr key={broadcast.id} className={broadcast.isScheduleOverdue ? 'table-danger' : undefined}>
                  <td>
                    <Link to={`${PUSH_BROADCAST_BASE_URL}/${broadcast.id}`}>{broadcast.name}</Link>
                    {broadcast.duplicatedFromId ? <small className="text-muted d-block">copy</small> : null}
                  </td>
                  <td>
                    <SolidBadge tone={PUSH_BROADCAST_STATUS_TONE[broadcast.status]}>{broadcast.status}</SolidBadge>
                    {broadcast.isScheduleOverdue ? (
                      <SolidBadge tone="red" className="ml-1">
                        OVERDUE: never started
                      </SolidBadge>
                    ) : null}
                  </td>
                  <td className="text-right">{recipientsCell(broadcast)}</td>
                  <td>
                    <div>{formatPushUtc(broadcast.createdAt)}</div>
                    <small className="text-muted">by {pushPersonLabel(broadcast.createdBy, people)}</small>
                  </td>
                  <td>{startedCell(broadcast, people)}</td>
                  <td className="text-right">
                    {PUSH_BROADCAST_FINISHED_STATUSES.includes(broadcast.status) ? (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={!!duplicatingId}
                        onClick={() => onDuplicate(broadcast.id)}
                        title="A new draft with the same texts, destination and category; the audience is not copied"
                      >
                        {duplicatingId === broadcast.id ? (
                          <Spinner animation="border" size="sm" className="mr-1" />
                        ) : null}
                        Duplicate
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
